import { create } from 'zustand'
import { supabase } from '@/infrastructure/supabase/client'
import type { User, Session } from '@supabase/supabase-js'
import type { Tenant, UserRole } from '@brix/types'

interface AuthState {
  user: User | null
  session: Session | null
  tenant: Tenant | null
  userRole: UserRole | null
  isLoading: boolean
  isInitialized: boolean

  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, captchaToken?: string) => Promise<void>
  signOut: () => Promise<void>
  setTenant: (tenant: Tenant, role: UserRole) => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  tenant: null,
  userRole: null,
  isLoading: false,
  isInitialized: false,

  initialize: async () => {
    const { data: { session } } = await supabase.auth.getSession()
    set({ session, user: session?.user ?? null, isInitialized: true })

    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null })
      if (!session) set({ tenant: null, userRole: null })
    })
  },

  signIn: async (email, password) => {
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
    } finally {
      set({ isLoading: false })
    }
  },

  signUp: async (email, password, captchaToken) => {
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      })
      if (error) throw error
    } finally {
      set({ isLoading: false })
    }
  },

  signOut: async () => {
    await supabase.auth.signOut()
    set({ user: null, session: null, tenant: null, userRole: null })
  },

  setTenant: (tenant, role) => {
    set({ tenant, userRole: role })
  },
}))

export const isAuthenticated = () => useAuthStore.getState().user !== null
